import { AlertCircle, Check, ChevronLeft, ChevronRight, CircleDashed, Copy, Inbox } from 'lucide-react';
import type { ReactNode } from 'react';
import { toast } from 'sonner';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Empty,
  EmptyContent,
  EmptyDescription,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
} from '@/components/ui/empty';
import { enumLabel } from '@/lib/labels';
import { NativeSelect, NativeSelectOption } from '@/components/ui/native-select';
import { Skeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';

export const PAGE_SIZE_OPTIONS = [10, 20, 40, 60, 100];

const successValues = new Set(['active', 'approved', 'success', 'low', 'mfa active']);
const warningValues = new Set(['invited', 'pending', 'medium']);
const dangerValues = new Set(['blocked', 'suspended', 'deactivated', 'failure', 'denied', 'rejected', 'high', 'critical', 'inactive']);

export function StatusBadge({ value }: { value: string }) {
  const normalized = value.toLowerCase();
  const className = successValues.has(normalized)
    ? 'border-green-200 bg-green-50 text-green-800 dark:border-green-900 dark:bg-green-950 dark:text-green-300'
    : warningValues.has(normalized)
      ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-900 dark:bg-amber-950 dark:text-amber-300'
      : dangerValues.has(normalized)
        ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300'
        : 'border-border bg-muted text-muted-foreground';

  return (
    <Badge variant="outline" className={cn(className)}>
      {enumLabel(value)}
    </Badge>
  );
}

export function EmptyState({
  title,
  detail,
  action,
  icon,
  className,
}: {
  title: string;
  detail: string;
  action?: ReactNode;
  icon?: ReactNode;
  className?: string;
}) {
  return (
    <Empty className={cn('min-h-64 border', className)}>
      <EmptyHeader>
        <EmptyMedia variant="icon">{icon ?? <Inbox />}</EmptyMedia>
        <EmptyTitle>{title}</EmptyTitle>
        <EmptyDescription>{detail}</EmptyDescription>
      </EmptyHeader>
      {action && <EmptyContent>{action}</EmptyContent>}
    </Empty>
  );
}

export function LoadingState({ rows = 3, className }: { rows?: number; className?: string }) {
  return (
    <div className={cn('flex flex-col gap-4', className)} aria-label="A carregar">
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <CircleDashed className="size-4 animate-spin" />
        A carregar…
      </div>
      {Array.from({ length: rows }).map((_, index) => (
        <Skeleton className="h-16 w-full" key={index} />
      ))}
    </div>
  );
}

export function FullPageLoading() {
  return (
    <div className="grid min-h-svh place-items-center bg-background">
      <LoadingState rows={0} />
    </div>
  );
}

export function PageHeader({
  title,
  description,
  badge,
  action,
}: {
  title: string;
  description: string;
  /** Estado da página; fica junto ao título para não competir com os botões em ecrãs estreitos. */
  badge?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-4 border-b pb-6 sm:flex-row sm:items-end sm:justify-between">
      <div className="flex min-w-0 flex-col gap-1">
        <div className="flex min-w-0 flex-wrap items-center gap-x-3 gap-y-2">
          <h1 className="min-w-0 truncate text-2xl font-semibold tracking-tight sm:text-3xl">{title}</h1>
          {badge}
        </div>
        <p className="max-w-3xl text-sm break-words text-muted-foreground sm:text-base">{description}</p>
      </div>
      {action && (
        <div className="flex w-full flex-wrap items-center gap-2 [&>*]:grow sm:w-auto sm:shrink-0 sm:[&>*]:grow-0">
          {action}
        </div>
      )}
    </div>
  );
}

export function PaginationFooter({
  page,
  pages,
  total,
  unit,
  onPageChange,
  pageSize,
  onPageSizeChange,
  pageSizeOptions = PAGE_SIZE_OPTIONS,
  className,
}: {
  page: number;
  pages: number;
  total: number;
  unit: string;
  onPageChange: (page: number) => void;
  pageSize?: number;
  onPageSizeChange?: (size: number) => void;
  pageSizeOptions?: number[];
  className?: string;
}) {
  if (total === 0) return null;
  const lastPage = Math.max(pages, 1);
  return (
    <div className={cn('flex flex-wrap items-center justify-between gap-3', className)}>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
        {pageSize !== undefined && onPageSizeChange && (
          <label className="flex items-center gap-2">
            <span>Por página</span>
            <NativeSelect
              size="sm"
              aria-label="Elementos por página"
              value={String(pageSize)}
              onChange={(event) => onPageSizeChange(Number(event.target.value))}
            >
              {pageSizeOptions.map((size) => <NativeSelectOption key={size} value={String(size)}>{size}</NativeSelectOption>)}
            </NativeSelect>
          </label>
        )}
        <span>Página {page} de {lastPage} · {total.toLocaleString('pt-PT')} {unit}</span>
      </div>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
          <ChevronLeft /> Anterior
        </Button>
        <Button variant="outline" size="sm" disabled={page >= lastPage} onClick={() => onPageChange(page + 1)}>
          Seguinte <ChevronRight />
        </Button>
      </div>
    </div>
  );
}

export function ErrorAlert({ message, title = 'Não foi possível concluir' }: { message: string; title?: string }) {
  return (
    <Alert variant="destructive">
      <AlertCircle />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription>{message}</AlertDescription>
    </Alert>
  );
}

export function SuccessAlert({ title, message }: { title: string; message: string }) {
  return (
    <Alert className="border-green-200 bg-green-50 text-green-900">
      <Check className="text-green-800" />
      <AlertTitle>{title}</AlertTitle>
      <AlertDescription className="text-green-800">{message}</AlertDescription>
    </Alert>
  );
}

export function CopyButton({ value, label = 'Copiar' }: { value: string; label?: string }) {
  return (
    <Button
      type="button"
      size="icon-sm"
      variant="ghost"
      aria-label={label}
      onClick={() => {
        void navigator.clipboard.writeText(value);
        toast.success('Copiado para a área de transferência');
      }}
    >
      <Copy />
    </Button>
  );
}

interface ConfirmProps {
  title: string;
  description: string;
  confirmLabel: string;
  onConfirm: () => void | Promise<void>;
  destructive?: boolean;
}

export function ConfirmAction({ trigger, ...confirm }: ConfirmProps & { trigger: ReactNode }) {
  return (
    <AlertDialog>
      <AlertDialogTrigger asChild>{trigger}</AlertDialogTrigger>
      <ConfirmBody {...confirm} />
    </AlertDialog>
  );
}

/**
 * Mesma confirmação sem trigger próprio, para quem a abre a partir de um menu: o item do menu
 * fecha-o ao ser escolhido, pelo que o diálogo tem de viver fora dele.
 */
export function ConfirmDialog({
  open,
  onOpenChange,
  ...confirm
}: ConfirmProps & { open: boolean; onOpenChange: (open: boolean) => void }) {
  return (
    <AlertDialog open={open} onOpenChange={onOpenChange}>
      <ConfirmBody {...confirm} />
    </AlertDialog>
  );
}

function ConfirmBody({ title, description, confirmLabel, onConfirm, destructive = true }: ConfirmProps) {
  return (
    <AlertDialogContent>
      <AlertDialogHeader>
        <AlertDialogTitle>{title}</AlertDialogTitle>
        <AlertDialogDescription>{description}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel>Cancelar</AlertDialogCancel>
        <AlertDialogAction
          variant={destructive ? 'destructive' : 'default'}
          onClick={() => void onConfirm()}
        >
          {confirmLabel}
        </AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  );
}
